import { useEffect, useState, useContext } from 'react';
import * as wasm from 'sched';
import { useDatabase } from './useDatabase';
import { WorkspaceContext } from '../context/WorkspaceContext';

export interface PersonDetails {
  id: string;
  name: string;
  avatarUrl?: string;
  teamIds: string[];
  remoteReferences: wasm.RemoteReference[];
}

/**
 * Hook to access person data
 */
export function usePerson(personId: string | undefined) {
  const { database, loading: databaseLoading } = useDatabase();
  const { safeData } = useContext(WorkspaceContext);
  const [person, setPerson] = useState<PersonDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!database || !personId || !safeData) {
      if (!database) setLoading(false);
      return;
    }

    try {
      const people = database.people();
      const personObj = people[personId];

      if (!personObj) {
        setError(`Person with ID ${personId} not found`);
        setPerson(null);
        setLoading(false);
      } else {
        // Fetch person data asynchronously
        const fetchPersonData = async () => {
          try {
            // Get team IDs and remote references
            const [teamIds, remoteRefs] = await Promise.all([
              personObj.team_ids(safeData),
              personObj.remote_references()
            ]);
            
            // Set person with all data
            setPerson({
              id: personId,
              name: personObj.name(),
              avatarUrl: personObj.avatar_url(),
              teamIds,
              remoteReferences: remoteRefs || []
            });
            setError(null);
          } catch (err) {
            console.error('Error fetching person data:', err);
            // Still set the person with basic data
            setPerson({
              id: personId,
              name: personObj.name(),
              avatarUrl: personObj.avatar_url(),
              teamIds: [],
              remoteReferences: []
            });
            setError(null);
          } finally {
            setLoading(false);
          }
        };
        
        fetchPersonData();
      }
    } catch (err) {
      console.error('Error fetching person:', err);
      setError(`Error fetching person: ${err}`);
      setPerson(null);
      setLoading(false);
    }
  }, [database, personId, safeData]);

  return { person, loading: loading || databaseLoading, error };
}
